import { createContext, useContext, useState, useCallback, useRef } from 'react';

const BasketContext = createContext(null);

let itemCounter = 0;
const nextItemId = () => `item_${++itemCounter}`;

export const BasketProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const lastParentRef = useRef(null);

  const addItem = useCallback((product) => {
    const productId = product.product_id || product.id;
    const itemId = nextItemId();

    setItems(prev => {
      // Check if same product already exists as a non-addon item
      const existing = prev.find(i => !i.is_addon && i.product_id === productId);
      if (existing) {
        lastParentRef.current = existing.item_id;
        return prev.map(i =>
          i.item_id === existing.item_id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }

      lastParentRef.current = itemId;
      return [...prev, {
        item_id: itemId,
        product_id: productId,
        product_name: product.product_name || product.name,
        unit_price: product.unit_price || product.price,
        unit_price_inc_vat: product.unit_price_inc_vat || product.price_inc_vat || product.unit_price || product.price,
        quantity: 1,
        is_addon: false,
        parent_item_id: null
      }];
    });

    return itemId;
  }, []);

  const addAddon = useCallback((product, parentItemId) => {
    const pid = parentItemId || lastParentRef.current;
    if (!pid) return;

    setItems(prev => {
      // Check if same addon already exists for this parent
      const existing = prev.find(
        i => i.is_addon && i.parent_item_id === pid && i.product_id === (product.product_id || product.id)
      );
      if (existing) {
        return prev.map(i =>
          i.item_id === existing.item_id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, {
        item_id: nextItemId(),
        product_id: product.product_id || product.id,
        product_name: product.product_name || product.name,
        unit_price: product.unit_price || product.price,
        unit_price_inc_vat: product.unit_price_inc_vat || product.price_inc_vat || product.unit_price || product.price,
        quantity: 1,
        is_addon: true,
        parent_item_id: pid
      }];
    });
  }, []);

  const removeItem = useCallback((itemId) => {
    setItems(prev => {
      const item = prev.find(i => i.item_id === itemId);
      if (!item) return prev;
      // If removing a parent, also remove its add-ons
      if (!item.is_addon) {
        return prev.filter(i => i.item_id !== itemId && i.parent_item_id !== itemId);
      }
      return prev.filter(i => i.item_id !== itemId);
    });
  }, []);

  const updateQuantity = useCallback((itemId, quantity) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }
    setItems(prev =>
      prev.map(item =>
        item.item_id === itemId ? { ...item, quantity } : item
      )
    );
  }, [removeItem]);

  const incrementItem = useCallback((itemId) => {
    setItems(prev =>
      prev.map(item =>
        item.item_id === itemId ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  }, []);

  const decrementItem = useCallback((itemId) => {
    setItems(prev => {
      const item = prev.find(i => i.item_id === itemId);
      if (!item) return prev;
      if (item.quantity <= 1) {
        // If parent, remove add-ons too
        if (!item.is_addon) {
          return prev.filter(i => i.item_id !== itemId && i.parent_item_id !== itemId);
        }
        return prev.filter(i => i.item_id !== itemId);
      }
      return prev.map(i =>
        i.item_id === itemId ? { ...i, quantity: i.quantity - 1 } : i
      );
    });
  }, []);

  const clearBasket = useCallback(() => {
    setItems([]);
    lastParentRef.current = null;
  }, []);

  const getLastParentId = useCallback(() => lastParentRef.current, []);

  const total = items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Group items: each parent followed by its add-ons
  const groupedItems = items.reduce((groups, item) => {
    if (!item.is_addon) {
      groups.push({ parent: item, addons: items.filter(a => a.is_addon && a.parent_item_id === item.item_id) });
    }
    return groups;
  }, []);

  return (
    <BasketContext.Provider value={{
      items,
      groupedItems,
      total,
      itemCount,
      addItem,
      addAddon,
      removeItem,
      updateQuantity,
      incrementItem,
      decrementItem,
      clearBasket,
      getLastParentId
    }}>
      {children}
    </BasketContext.Provider>
  );
};

export const useBasket = () => {
  const context = useContext(BasketContext);
  if (!context) {
    throw new Error('useBasket must be used within BasketProvider');
  }
  return context;
};
